import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 8 * 1024;
const ACCOUNT_PATH = "/api/v1/account/loot-rate-display-preference";
const MODES = Object.freeze([
  "normalized",
  "denormalized",
  "rawWeight",
]);
const COMMAND_FIELDS = Object.freeze({
  create: ["kind", "schemaVersion", "operationId", "mode"],
  set: [
    "kind",
    "schemaVersion",
    "operationId",
    "expectedRevision",
    "mode",
  ],
  reset: ["kind", "schemaVersion", "operationId", "expectedRevision"],
});

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactFields(value, expectedFields) {
  if (!isRecord(value)) return false;
  const actualFields = Object.keys(value).sort();
  const sortedExpected = expectedFields.slice().sort();
  return actualFields.length === sortedExpected.length
    && actualFields.every((field, index) => field === sortedExpected[index]);
}

function positiveSafeInteger(value) {
  return Number.isSafeInteger(value) && value > 0;
}

export function decodeLootRateDisplayModeV1(value) {
  if (!MODES.includes(value)) {
    throw new TypeError("Account loot-rate display mode is invalid.");
  }
  return value;
}

function freezeProjection(value) {
  return value.kind === "missing"
    ? Object.freeze({ kind: "missing", schemaVersion: SCHEMA_VERSION })
    : Object.freeze({
      kind: "ready",
      schemaVersion: SCHEMA_VERSION,
      revision: value.revision,
      mode: decodeLootRateDisplayModeV1(value.mode),
    });
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritativeState) {
    next.authoritativeState = freezeProjection(next.authoritativeState);
  }
  return Object.freeze(next);
}

export function decodeLootRateDisplayPreferenceProjectionV1(value) {
  if (
    hasExactFields(value, ["kind", "schemaVersion"])
    && value.kind === "missing"
    && value.schemaVersion === SCHEMA_VERSION
  ) {
    return freezeProjection(value);
  }
  if (
    !hasExactFields(value, ["kind", "schemaVersion", "revision", "mode"])
    || value.kind !== "ready"
    || value.schemaVersion !== SCHEMA_VERSION
    || !positiveSafeInteger(value.revision)
  ) {
    throw new TypeError(
      "Account loot-rate display preference projection is invalid.",
    );
  }
  return freezeProjection(value);
}

export function decodeLootRateDisplayPreferenceCommandV1(value) {
  const expectedFields = COMMAND_FIELDS[value?.kind];
  if (
    !expectedFields
    || !hasExactFields(value, expectedFields)
    || value.schemaVersion !== SCHEMA_VERSION
    || !isAccountOperationId(value.operationId)
    || (
      ["set", "reset"].includes(value.kind)
      && !positiveSafeInteger(value.expectedRevision)
    )
  ) {
    throw new TypeError(
      "Account loot-rate display preference command is invalid.",
    );
  }
  return Object.freeze({
    ...value,
    ...(["create", "set"].includes(value.kind)
      ? { mode: decodeLootRateDisplayModeV1(value.mode) }
      : {}),
  });
}

export function decodeLootRateDisplayPreferenceAcknowledgementV1(
  value,
  operationId,
) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError(
      "Account loot-rate display preference acknowledgement is invalid.",
    );
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState:
      decodeLootRateDisplayPreferenceProjectionV1(value.authoritativeState),
  });
}

function accountUrl(apiBaseUrl) {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Loot-rate display preference API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError(
      "Loot-rate display preference API base URL must be an HTTP origin.",
    );
  }
  return `${base.origin}${ACCOUNT_PATH}`;
}

export function createLootRateDisplayPreferenceAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Loot-rate display preference provider requires fetch.");
  }
  const url = accountUrl(apiBaseUrl);

  async function load({ signal } = {}) {
    const response = await fetchImpl(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (!response.ok) {
      throw new Error("Loot-rate display preference request failed.");
    }
    return decodeLootRateDisplayPreferenceProjectionV1(
      await readBoundedAccountJson(
        response,
        "Account loot-rate display preference",
        RESPONSE_MAX_BYTES,
      ),
    );
  }

  async function mutate(session, command, { signal } = {}) {
    const exactCommand = decodeLootRateDisplayPreferenceCommandV1(command);
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(exactCommand),
    });
    if (response.status === 401) return freezeState({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Loot-rate display preference mutation failed.");
    }
    const acknowledgement = decodeLootRateDisplayPreferenceAcknowledgementV1(
      await readBoundedAccountJson(
        response,
        "Account loot-rate display preference acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      exactCommand.operationId,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError(
        "Loot-rate display preference status and acknowledgement disagree.",
      );
    }
    return acknowledgement;
  }

  return Object.freeze({ load, mutate });
}

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function expectedRevision(options, label) {
  if (options === undefined) return undefined;
  if (
    !isRecord(options)
    || Object.keys(options).some((field) => field !== "expectedRevision")
  ) {
    throw new TypeError(`${label} options contain unsupported fields.`);
  }
  if (options.expectedRevision === undefined || options.expectedRevision === null) {
    return options.expectedRevision;
  }
  if (!positiveSafeInteger(options.expectedRevision)) {
    throw new TypeError(`${label} expected revision is invalid.`);
  }
  return options.expectedRevision;
}

export function createAuthenticatedLootRateDisplayPreferenceRepository({
  sessionRepository,
  accountProvider = null,
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (!hasMethods(sessionRepository, ["load", "refresh", "state", "subscribe"])) {
    throw new TypeError(
      "Loot-rate display preference requires the account session repository.",
    );
  }
  const remote = accountProvider
    ?? createLootRateDisplayPreferenceAccountProvider({ apiBaseUrl, fetchImpl });
  if (
    !hasMethods(remote, ["load", "mutate"])
    || typeof createOperationId !== "function"
  ) {
    throw new TypeError(
      "Loot-rate display preference requires its typed account provider.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authoritativeState = null;
  let accountId = null;
  let requestId = 0;
  let requestController = null;
  let sessionLoadDepth = 0;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(
        new Error("Loot-rate display preference subscriber failed."),
      );
    }
  }

  function publish(value) {
    if (disposed) return currentState;
    const nextState = freezeState(value);
    if (JSON.stringify(currentState) === JSON.stringify(nextState)) {
      return currentState;
    }
    currentState = nextState;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function beginRequest({ loading = false } = {}) {
    requestId += 1;
    requestController?.abort();
    requestController = new AbortController();
    if (loading) publish({ kind: "loading" });
    return Object.freeze({ id: requestId, controller: requestController });
  }

  function isCurrent(request) {
    return !disposed
      && request.id === requestId
      && !request.controller.signal.aborted;
  }

  function clearAuthority(value) {
    authoritativeState = null;
    accountId = null;
    return publish(value);
  }

  function applyProjection(projection, nextAccountId) {
    authoritativeState = projection;
    accountId = nextAccountId;
    return publish({ kind: "ready", authoritativeState: projection });
  }

  function unavailable(operation, reason) {
    return clearAuthority({ kind: "unavailable", operation, reason });
  }

  async function freshSession(request, { refresh = false } = {}) {
    sessionLoadDepth += 1;
    try {
      const session = await sessionRepository[refresh ? "refresh" : "load"]();
      return isCurrent(request) ? session : null;
    } finally {
      sessionLoadDepth -= 1;
    }
  }

  function applySessionBoundary(session) {
    if (session?.kind === "signedOut") {
      return clearAuthority({ kind: "signedOut" });
    }
    if (session?.kind === "locked") {
      return clearAuthority({
        kind: "locked",
        reason: session.reason || "account-locked",
      });
    }
    if (session?.kind === "unavailable") {
      return unavailable("account", session.reason || "session-request-failed");
    }
    if (session?.kind !== "ready") {
      return clearAuthority({ kind: "loading" });
    }
    return null;
  }

  async function readRemote(request, session) {
    try {
      const projection = await remote.load({ signal: request.controller.signal });
      if (!isCurrent(request)) return state();
      if (projection.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") {
          return state();
        }
        return unavailable("load", "account-authorization-rejected");
      }
      return applyProjection(projection, session.accountId);
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable("load", "loot-rate-display-request-failed");
    }
  }

  async function load() {
    if (disposed) return state();
    const request = beginRequest({ loading: true });
    const session = await freshSession(request);
    if (!session || !isCurrent(request)) return state();
    if (applySessionBoundary(session) || session.kind !== "ready") return state();
    return readRemote(request, session);
  }

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      throw new TypeError("Loot-rate display preference operation ID is invalid.");
    }
    return value;
  }

  function commandFor(nextMode, reset) {
    if (!authoritativeState) return null;
    if (reset) {
      return authoritativeState.kind === "missing"
        ? null
        : decodeLootRateDisplayPreferenceCommandV1({
          kind: "reset",
          schemaVersion: SCHEMA_VERSION,
          operationId: operationId(),
          expectedRevision: authoritativeState.revision,
        });
    }
    return decodeLootRateDisplayPreferenceCommandV1({
      kind: authoritativeState.kind === "missing" ? "create" : "set",
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      ...(authoritativeState.kind === "ready"
        ? { expectedRevision: authoritativeState.revision }
        : {}),
      mode: nextMode,
    });
  }

  function conflict(operation, expected) {
    return publish({
      kind: "conflict",
      operation,
      expectedRevision: expected,
      authoritativeState,
    });
  }

  async function mutate(operation, nextMode, options, { reset = false } = {}) {
    const expected = expectedRevision(options, `Loot-rate display ${operation}`);
    if (currentState.kind === "pending") return state();
    if (!authoritativeState || !accountId) return state();
    const currentRevision = authoritativeState.kind === "ready"
      ? authoritativeState.revision
      : null;
    if (
      !reset
      && authoritativeState.kind === "ready"
      && authoritativeState.mode === nextMode
    ) {
      return state();
    }
    if (expected !== undefined && expected !== currentRevision) {
      return conflict(operation, expected);
    }
    const command = commandFor(nextMode, reset);
    if (!command) return state();
    const startingAccountId = accountId;
    const request = beginRequest();
    publish({ kind: "pending", operation, authoritativeState });

    try {
      const session = await freshSession(request, { refresh: true });
      if (!session || !isCurrent(request)) return state();
      if (applySessionBoundary(session) || session.kind !== "ready") return state();
      if (session.accountId !== startingAccountId) {
        return unavailable(operation, "account-authority-changed");
      }
      const acknowledgement = await remote.mutate(session, command, {
        signal: request.controller.signal,
      });
      if (!isCurrent(request)) return state();
      if (acknowledgement.kind === "signedOut") {
        const refreshed = await freshSession(request, { refresh: true });
        if (!refreshed || !isCurrent(request)) return state();
        if (applySessionBoundary(refreshed) || refreshed.kind !== "ready") {
          return state();
        }
        return unavailable(operation, "account-authorization-rejected");
      }
      authoritativeState = acknowledgement.authoritativeState;
      accountId = session.accountId;
      if (acknowledgement.kind === "conflict") {
        return publish({
          kind: "conflict",
          operation,
          expectedRevision: currentRevision,
          authoritativeState,
        });
      }
      return publish({ kind: "ready", authoritativeState });
    } catch (error) {
      if (!isCurrent(request) || error?.name === "AbortError") return state();
      return unavailable(operation, "loot-rate-display-mutation-failed");
    }
  }

  function setMode(mode, options) {
    return mutate(
      "setMode",
      decodeLootRateDisplayModeV1(mode),
      options,
    );
  }

  function reset(options) {
    return mutate("reset", null, options, { reset: true });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError(
        "Loot-rate display preference listener must be a function.",
      );
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  const unsubscribeSession = sessionRepository.subscribe((session) => {
    if (disposed || sessionLoadDepth > 0) return;
    if (session.kind !== "ready") {
      beginRequest();
      applySessionBoundary(session);
      return;
    }
    const request = beginRequest({ loading: true });
    void readRemote(request, session);
  });

  function dispose() {
    if (disposed) return;
    disposed = true;
    requestId += 1;
    requestController?.abort();
    requestController = null;
    listeners.clear();
    unsubscribeSession();
  }

  return Object.freeze({
    dispose,
    load,
    reset,
    setMode,
    state,
    subscribe,
  });
}
